import React, { useState } from "react";
import { ListProps, useQuery } from "react-admin";
import {Card, Col, Row, Select, Typography, Space, DatePicker} from "antd";
import { Widget } from "@src/ui/widget";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {getIconCost, getIconLatency, getIconPipeline, getIconResponse, getIconToken} from "./utils";
import type { Dayjs } from 'dayjs';
import {currencyFormatter, CustomPriceTag, getCurrency} from "@src/components/Common/utils";
import {BaseFilterDropdown} from "@src/components/Filters/BaseFilterDropdown";
import PackDashboardTabs from "../Pack/PackDashboard/PackDashboardTabs";
import FiltersPopupWrapper from "@src/components/Filters/FiltersPopup";

const { Option } = Select;
const { RangePicker } = DatePicker;


const AIIntegrationListView = (_props: ListProps) => {
    const currency = getCurrency()

    const resource = "serviceowner_pack_statistics";

    // Filter state management
    const [selectedService, setSelectedService] = useState<number | null>(null);
    const [selectedLlmModel, setSelectedLlmModel] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState<any>(null);
    const [timeInterval, setTimeInterval] = useState<string>("daily");

    type ChartId = 'cost' | 'calls' | 'avgCost' | 'tokens';
    const [activeChart, setActiveChart] = useState<ChartId>('avgCost');
    const [bigChartVisible, setBigChartVisible] = useState(true);

    // Dynamic x-axis format based on time interval
    const getXAxisFormat = () => {
        switch (timeInterval) {
            case 'hourly': return 'HH:mm';
            case 'daily': return 'MMM dd';
            case 'weekly': return 'MMM dd';
            case 'monthly': return 'MMM yyyy';
            default: return 'MMM dd';
        }
    };
    const xAxisFormat = getXAxisFormat();

    const swapActiveChart = (next: ChartId) => {
        if (next === activeChart) return;
        // Simple fade/slide animation without extra packages
        setBigChartVisible(false);
        window.setTimeout(() => {
            setActiveChart(next);
            setBigChartVisible(true);
        }, 160);
    };

    // Build filter object for API
    const graphFilter: Record<string, any> = {};
    if (selectedService) graphFilter.service_id = selectedService;
    if (selectedLlmModel) graphFilter.llm_model_id = selectedLlmModel;
    if (dateRange) {
        if (dateRange[0]) {
            graphFilter.start_date = dateRange[0].format('YYYY-MM-DD');
        }
        if (dateRange[1]) {
            graphFilter.end_date = dateRange[1].format('YYYY-MM-DD');
        }
    }
    if (timeInterval) graphFilter.time_interval = timeInterval;

    const { data: graphData } = useQuery({
        type: 'getAction',
        resource: resource,
        payload: { id: null, verb: 'graphs', filter: graphFilter}
    }, {});

    const baseStatisticsEndpoint = 'serviceowner_statistics'
    const { data: AverageCostGraphData } = useQuery({
        type: 'getAction',
        resource: baseStatisticsEndpoint,
        payload: { id: null, verb: 'pack_cost_average_graph', filter: graphFilter}
    }, {});

    const { data: basicsData } = useQuery({
        type: 'getAction',
        resource: resource,
        payload: { id: null, verb: 'basics', filter: graphFilter}
    }, {});

    // Transform graph data for charts
    let chartData = graphData && graphData?.results && graphData?.results.map((item: any) => ({

        date: item[0],
        count: item[1]?.count || 0,
        cost: item[1]?.cost || 0,
        tokens: item[1]?.total_tokens || 0,
    }));
    if(!chartData)
        chartData = []

    // Transform average cost graph data
    let avgCostChartData = AverageCostGraphData && AverageCostGraphData?.results && AverageCostGraphData?.results.map((item: any) => ({
        date: item[0],
        avgCost: item[1]?.aiprs_avg || 0,
    }))
    if(!avgCostChartData)
        avgCostChartData = []

    // Chart Options for Total Spend (Cost)
    const costChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#389e0d'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => currencyFormatter(currency).format(value || 0),
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `$${value.toFixed(2)}`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const costChartSeries = [{
        name: 'Cost',
        data: chartData.map((d: any) => d.cost),
    }];

    // Chart Options for LLM Calls (Count)
    const callsChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#08979c'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => value.toFixed(0),
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `${value} calls`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const callsChartSeries = [{
        name: 'Calls',
        data: chartData.map((d: any) => d.count),
    }];

    // Chart Options for Token Count
    const tokensChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#faad14'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => `${(value / 1000).toFixed(0)}K`,
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `${value.toLocaleString()} tokens`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const tokensChartSeries = [{
        name: 'Tokens',
        data: chartData.map((d: any) => d.tokens),
    }];

    // Chart Options for Average Cost
    const avgCostChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#1890ff'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'category',
            categories: avgCostChartData.map((d: any) => {
                const date = new Date(d.date);
                if (timeInterval === 'hourly') {
                    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                } else if (timeInterval === 'monthly') {
                    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                }
                return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
            }),
            labels: {
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => currencyFormatter(currency).format(value || 0),
            }
        },
        tooltip: {
            enabled: true,
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `$${value.toFixed(4)}`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const avgCostChartSeries = [{
        name: 'Average Cost',
        data: avgCostChartData.map((d: any) => d.avgCost),
    }];

    const getChartMeta = (id: ChartId) => {
        switch (id) {
            case 'cost':
                return { title: 'Token Costs', icon: getIconCost(), type: 'bar' as const, options: costChartOptions, series: costChartSeries };
            case 'calls':
                return { title: 'LLM Calls', icon: getIconResponse(), type: 'bar' as const, options: callsChartOptions, series: callsChartSeries };
            case 'avgCost':
                return { title: 'Average Cost per Change Instance', icon: getIconCost(), type: 'bar' as const, options: avgCostChartOptions, series: avgCostChartSeries };
            case 'tokens':
                return { title: 'Token Count', icon: getIconToken(), type: 'bar' as const, options: tokensChartOptions, series: tokensChartSeries };
        }
    };

    const renderChart = (id: ChartId, size: 'big' | 'mini') => {
        const meta = getChartMeta(id);
        const isBig = size === 'big';
        const isActive = id === activeChart;
        // Make minor charts compact, but ensure the stacked minor charts align with the big chart height.
        // Note: each chart includes a header above the plot; the big chart has 1 header, while the right column has 3.
        const gapBetweenMiniCharts = 10;
        const bigHeight = 480; // plot height for the big chart
        const estimatedMiniHeaderHeight = 30; // approx header height per mini chart (title row)
        const computedMiniHeight = Math.floor((bigHeight - (gapBetweenMiniCharts * 2) - (estimatedMiniHeaderHeight * 2)) / 3);
        const miniHeight = Math.max(130, computedMiniHeight); // keep a sensible minimum
        const height = isBig ? bigHeight : miniHeight;

        const miniOptions: ApexOptions = isBig
            ? {
                ...meta.options,
                chart: { ...(meta.options.chart || {}), height },
            }
            : {
                ...meta.options,
                chart: { ...(meta.options.chart || {}), height, toolbar: { show: false } },
                // Show labels on axes for minor charts (requested)
                xaxis: { ...(meta.options.xaxis || {}), labels: { ...((meta.options.xaxis as any)?.labels || {}), show: true } },
                yaxis: { ...(meta.options.yaxis || {}), labels: { ...((meta.options.yaxis as any)?.labels || {}), show: true } },
                grid: { ...(meta.options.grid || {}), padding: { left: 6, right: 6, top: 0, bottom: 0 } as any },
                tooltip: meta.options.tooltip,
            };

        // Big chart container animation
        const animatedStyle: React.CSSProperties = isBig
            ? {
                opacity: bigChartVisible ? 1 : 0,
                transform: bigChartVisible ? 'translateY(0px)' : 'translateY(8px)',
                transition: 'opacity 250ms ease, transform 250ms ease',
            }
            : {};

        // Always show the interval selector on the big chart header
        const headerRight = isBig ? (
            <Select
                size="small"
                value={timeInterval}
                onChange={(value) => setTimeInterval(value)}
                style={{ width: 110 }}
                className="filter-select"
            >
                <Option value="hourly">Hourly</Option>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
            </Select>
        ) : null;

        const wrapperStyle: React.CSSProperties = {
            background: 'hsl(0 0% 100%)',
            borderRadius: '8px',
            // remove inner whitespace for mini charts
            padding: isBig ? '8px' : '0px',
            border: isBig ? '1px solid hsl(0 0% 89.8%)' : (isActive ? '1px solid #1890ff' : '1px solid hsl(0 0% 89.8%)'),
            cursor: isBig ? 'default' : 'pointer',
            boxShadow: isBig ? 'none' : (isActive ? '0 0 0 2px rgba(24, 144, 255, 0.15)' : 'none'),
            transition: 'box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease',
        };

        return (
            <div
                role={isBig ? undefined : 'button'}
                aria-label={isBig ? undefined : `Show ${meta.title}`}
                title={isBig ? undefined : 'Click to zoom'}
                onClick={isBig ? undefined : () => swapActiveChart(id)}
                style={{ width: '100%', userSelect: 'none' }}
            >
                <div
                    style={{
                        marginBottom: isBig ? '4px' : '0px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Space align="center" size={4}>
                        <span>{meta.icon}</span>
                        <h3
                            style={{
                                fontSize: isBig ? '16px' : '14px',
                                fontWeight: 600,
                                margin: 0,
                                color: 'hsl(220 40% 25%)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {meta.title}
                        </h3>
                    </Space>
                    {headerRight}
                </div>

                <div style={{ ...wrapperStyle, ...(isBig ? animatedStyle : {}) }}>
                    {((id === 'avgCost' ? avgCostChartData.length : chartData.length) > 0) ? (
                        <ReactApexChart
                            key={isBig ? `big-${activeChart}` : `mini-${id}`}
                            options={miniOptions}
                            series={meta.series}
                            type={meta.type}
                            height={height}
                        />
                    ) : (
                        <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography.Text type="secondary">No data available</Typography.Text>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Card
            bordered={false}
            className="pack-card"
            bodyStyle={{ padding: 0, height: "100%" }}
        >
            {/* Main Content Area */}
            <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                    <Space orientation="vertical" size={10} style={{ width: "100%", display: "flex"}}>
                            {/* Filters and Widgets Row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0px', padding: '8px', flexWrap: 'wrap' }}>
                                <FiltersPopupWrapper>
                                    <div style={{ display: 'flex', gap: '16px', padding: '8px 16px', flexWrap: 'wrap' }}>
                                        <BaseFilterDropdown
                                            // title="Service"
                                            allTitle="All Services"
                                            placeholder="Select service"
                                            value={selectedService}
                                            onChange={(value) => setSelectedService(value)}
                                            resource="services"
                                        />
                                        <BaseFilterDropdown
                                            // title="LLM Model"
                                            allTitle="All Models"
                                            placeholder="Select LLM model"
                                            value={selectedLlmModel}
                                            onChange={(value) => setSelectedLlmModel(value)}
                                            resource="llm_models"
                                        />
                                        <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                                            <label className="filter-label">
                                                Date Range
                                            </label>
                                            <RangePicker
                                                style={{ width: "400px", borderRadius: "8px" }}
                                                value={dateRange}
                                                onChange={(dates) => setDateRange(dates)}
                                            />
                                        </div>
                                    </div>
                                </FiltersPopupWrapper>

                                <Row gutter={[6, 12]} style={{ flex: 1 }}>
                                    <Col xs={12} sm={12} md={5}>
                                        <Widget
                                            title="Total Pipelines"
                                            value={basicsData?.total_pipelines || 0}
                                            icon={getIconPipeline()}
                                            status="info"
                                            loading={!basicsData}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <Widget
                                            title="Total Cost"
                                            value={CustomPriceTag(basicsData?.total_cost || 0)}
                                            icon={getIconCost()}
                                            status="success"
                                            loading={!basicsData}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <Widget
                                            title="Total LLM calls"
                                            value={basicsData?.total_llm_calls || 0}
                                            icon={getIconResponse()}
                                            status="default"
                                            loading={!basicsData}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={7}>
                                        <Widget
                                            title="Average Response Time"
                                            value={basicsData?.avg_latency_ms ? (basicsData.avg_latency_ms / 1000).toFixed(2) : 0}
                                            valueSuffix="s"
                                            icon={getIconLatency()}
                                            status="default"
                                            loading={!basicsData}
                                            inline
                                        />
                                    </Col>
                                </Row>
                            </div>

                        </Space>
            </div>

            {/* Graph Section - 1 Big + 3 Small (click to swap) */}
            <Row style={{ width: "100%" }}>
                <div
                    style={{
                        width: "100%",
                        background: "#ffffff",
                        padding: "10px 10px",
                        border: "1px solid hsl(0 0% 89.8%)",
                    }}
                >
                    <Row gutter={[8, 8]} align="top">
                        {/* Big Chart */}
                        <Col xs={24} lg={17}>
                            {renderChart(activeChart, 'big')}
                        </Col>

                        {/* Minor Charts (right side) */}
                        <Col xs={24} lg={7}>
                            <Space orientation="vertical" size={8} style={{ width: '100%', paddingTop: '8px' }}>
                                {(['cost', 'calls', 'avgCost', 'tokens'] as ChartId[])
                                    .filter((id) => id !== activeChart)
                                    .slice(0, 3)
                                    .map((id) => (
                                        <div
                                            key={id}
                                            style={{
                                                width: '100%',
                                                transform: 'translateY(0px)',
                                            }}
                                            onMouseEnter={(e) => {
                                                // subtle hover lift without extra packages
                                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0px)';
                                            }}
                                        >
                                            {renderChart(id, 'mini')}
                                        </div>
                                    ))}
                            </Space>
                        </Col>
                    </Row>
                </div>
            </Row>

        </Card>
    );
};

const AIIntegrationList = (props: ListProps) => {
    return (
        <>
            <PackDashboardTabs />
            <AIIntegrationListView />
        </>
    )
}

export default AIIntegrationList;
